import { Box, Button, IconButton, Paper } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GamePositionsRepresentation } from '@/features/autoIso/components';
import { DragIndicator, PushPin, PushPinOutlined, SouthWest, NorthEast } from '@mui/icons-material';
import { usePersistedValue } from '../hooks/usePersistedValue';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';

type DraggableGamePositionProps = {
  isFullscreen: boolean;
  // Add any other props GamePositionsRepresentation needs
};

type Position = {
  x: number;
  y: number;
};

type DockPosition = 'topRight' | 'topLeft' | 'bottomRight' | 'bottomLeft' | 'none';

// Width of the docked map. GamePositionsRepresentation sizes the surface to fit
// whatever box it is handed (it measures its own wrapper), so this Paper has to
// supply a definite one: it is absolutely positioned and shrink-to-fit, which
// makes the wrapper's `flex: 1` inert and collapses its measured height to 0 —
// and a 0-height wrapper renders the surface at 0x0. The side panel does not hit
// this because it nests the surface in a full-height flex column.
const MAP_WIDTH = 320;

const DraggableGamePosition: React.FC<DraggableGamePositionProps> = ({ isFullscreen }) => {
  // Store component position in state
  const [position, setPosition] = useState<Position>({ x: 200, y: 300 });
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = usePersistedValue<boolean>('gamePositionMapVisible', true);
  const [isDocked, setIsDocked] = usePersistedValue<boolean>('gamePositionMapDocked', true);
  const [dockPosition, setDockPosition] = usePersistedValue<DockPosition>(
    'gamePositionMapDockPosition',
    'topRight',
  );
  const [isAnimatingHide, setIsAnimatingHide] = useState(false);
  const { singleCurrentlyTrackedObjectId, trackEntity } = useServerStateContext();

  // The object of play is sport-specific ("Puck" for NHL, "Ball" for NBA). It
  // comes from the game's type metadata (backend), same source AutoISOTeamSelector
  // uses — no league-name matching required.
  const { objectOfPlay, surfaceWidth, surfaceHeight } = useCurrentGame();

  const dragRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const originalPositionRef = useRef<Position>({ x: 0, y: 0 });

  // Calculate position based on docking status
  const getPositionStyle = useCallback(() => {
    if (isDocked) {
      switch (dockPosition) {
        case 'topRight':
          return { top: 20, right: 20, bottom: 'auto', left: 'auto' };
        case 'topLeft':
          return { top: 20, left: 20, bottom: 'auto', right: 'auto' };
        case 'bottomRight':
          return { bottom: 20, right: 20, top: 'auto', left: 'auto' };
        case 'bottomLeft':
          return { bottom: 20, left: 20, top: 'auto', right: 'auto' };
        default:
          return { top: 20, right: 20, bottom: 'auto', left: 'auto' };
      }
    } else {
      return { top: position.y, left: position.x, bottom: 'auto', right: 'auto' };
    }
  }, [isDocked, dockPosition, position]);

  // Handle mouse down to start dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isDocked) return;

    setIsDragging(true);

    // Store initial mouse position and element position
    startPosRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  // Handle mouse move during dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Calculate new position
      const newX = e.clientX - startPosRef.current.x;
      const newY = e.clientY - startPosRef.current.y;

      // Update position
      setPosition({ x: newX, y: newY });

      // Check for docking
      const container = containerRef.current?.getBoundingClientRect();
      if (container) {
        // Detect proximity to edges for docking
        const threshold = 30;

        if (newX < threshold) {
          // Left edge
          if (newY < container.height / 2) {
            setDockPosition('topLeft');
            setIsDocked(true);
          } else {
            setDockPosition('bottomLeft');
            setIsDocked(true);
          }
        } else if (newX > container.width - threshold - (dragRef.current?.offsetWidth || 0)) {
          // Right edge
          if (newY < container.height / 2) {
            setDockPosition('topRight');
            setIsDocked(true);
          } else {
            setDockPosition('bottomRight');
            setIsDocked(true);
          }
        }
      }
    },
    [isDragging, setPosition, setDockPosition, setIsDocked],
  );

  // Handle mouse up to stop dragging
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Setup event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Only show in fullscreen mode. This has to sit below every hook above:
  // returning early on a prop that flips while the component stays mounted
  // changes the hook count between renders, which React rejects outright
  // ("Rendered more hooks than during the previous render").
  if (!isFullscreen) return null;

  // Shaped to the surface's own aspect ratio so it is not letterboxed inside
  // the box above. Zero until the game's surface dimensions have loaded, which
  // is also when the surface itself has nothing to draw.
  const mapHeight =
    surfaceWidth && surfaceHeight ? Math.round(MAP_WIDTH * (surfaceHeight / surfaceWidth)) : 0;

  // Toggle visibility with animation
  const toggleVisibility = () => {
    if (isVisible) {
      if (dragRef.current) {
        // Calculate where the component needs to go (top-right with 20px margin)
        const rect = dragRef.current.getBoundingClientRect();
        const targetX = window.innerWidth - rect.width - 20;
        const targetY = 20;

        // Calculate the distance to travel
        const deltaX = targetX - rect.left;
        const deltaY = targetY - rect.top;

        // Store the translation values for the animation
        dragRef.current.style.setProperty('--target-x', `${deltaX}px`);
        dragRef.current.style.setProperty('--target-y', `${deltaY}px`);
      }

      // Start hide animation
      setIsAnimatingHide(true);

      // After animation completes, actually hide
      setTimeout(() => {
        setIsVisible(false);
        setIsAnimatingHide(false);
      }, 300); // Match this with the animation duration
    } else {
      // Show immediately
      setIsVisible(true);
    }
  };

  // Toggle docking
  const toggleDock = () => {
    if (!isDocked) {
      switch (dockPosition) {
        case 'topRight':
          setPosition({ x: window.innerWidth - dragRef.current!.offsetWidth - 40, y: 40 });
          break;
        case 'topLeft':
          setPosition({ x: 40, y: 40 });
          break;
        case 'bottomRight':
          setPosition({
            x: window.innerWidth - dragRef.current!.offsetWidth - 40,
            y: window.innerHeight - dragRef.current!.offsetHeight - 40,
          });
          break;
        case 'bottomLeft':
          setPosition({ x: 40, y: window.innerHeight - dragRef.current!.offsetHeight - 40 });
          break;
        default:
          break;
      }
    }

    setIsDocked(!isDocked);
  };

  if (!isVisible && !isDragging && !isAnimatingHide) {
    // Show only the visibility toggle when hidden
    return (
      <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 100 }}>
        <IconButton
          onClick={toggleVisibility}
          sx={{
            backgroundColor: '#00E67649',
            color: '#00E676',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
          }}
        >
          <SouthWest />
        </IconButton>
      </Box>
    );
  }

  // Get animation styles
  const getAnimationStyle = () => {
    if (isAnimatingHide) {
      return {
        transform: 'translate(var(--target-x, 0px), var(--target-y, 0px)) scale(0.5)',
        opacity: 0,
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)', // Smooth easing
      };
    }
    return {};
  };

  return (
    <Box
      ref={containerRef}
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      zIndex={100}
      sx={{ pointerEvents: 'none' }}
    >
      <Paper
        ref={dragRef}
        elevation={3}
        sx={{
          position: 'absolute',
          ...getPositionStyle(),
          ...getAnimationStyle(),
          pointerEvents: 'auto',
          backgroundColor: 'rgba(30, 30, 30, 0.8)',
          borderRadius: '4px',
          transition: isDragging ? 'none' : 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isDragging ? 0.9 : isAnimatingHide ? 0 : 0.6,
          transformOrigin: 'center center',
          '&:hover': {
            opacity: isAnimatingHide ? 0 : 0.9,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '4px',
            cursor: isDocked ? 'default' : 'move',
          }}
          onMouseDown={handleMouseDown}
        >
          <DragIndicator sx={{ color: 'white', fontSize: 18, opacity: isDocked ? 0.5 : 1 }} />
          <Box>
            <IconButton size="small" onClick={toggleDock} sx={{ color: 'white' }}>
              {isDocked ? (
                <PushPin sx={{ rotate: '45deg' }} fontSize="small" />
              ) : (
                <PushPinOutlined fontSize="small" />
              )}
            </IconButton>
            <IconButton size="small" onClick={toggleVisibility} sx={{ color: 'white' }}>
              <NorthEast fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ padding: 1 }}>
          <Box sx={{ width: MAP_WIDTH, height: mapHeight, display: 'flex' }}>
            {/* `p: 0` drops the surface's own inset — the padding above already
                insets it, and this map has no room to spare. */}
            <GamePositionsRepresentation sx={{ p: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
            <Button
              variant={singleCurrentlyTrackedObjectId === 1 ? 'contained' : 'outlined'}
              onClick={() => {
                trackEntity({ id: 1 }, 'Fullscreen Puck')
              }}
            >
              {objectOfPlay || 'Puck'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default DraggableGamePosition;
